/**
 * Talking to the coach instead of typing at it.
 *
 * Recording is done with MediaRecorder and transcribed server-side by Whisper,
 * rather than with the browser's own SpeechRecognition: that API is Chrome-only
 * on desktop, ships audio to Google regardless of where the rest of the app
 * talks to, and is poor at chess words. "Rook takes c2" comes back as "Rooke
 * takes see two" often enough to be useless.
 *
 * The level meter is read from the live stream rather than faked on a timer,
 * so a muted or dead microphone looks dead instead of looking like it is
 * listening.
 */

export type Recorder = {
  stop: () => Promise<Blob | null>;
  cancel: () => void;
  /** Current input level, 0..1, for the waveform. */
  level: () => number;
};

export async function startRecording(): Promise<Recorder> {
  const stream = await navigator.mediaDevices.getUserMedia({audio: true});

  // Level metering. Wrapped because Safari names it webkitAudioContext, and
  // because an AudioContext can fail to start without the page being
  // considered "user activated" — in which case the meter simply reads 0
  // rather than taking the recording down with it.
  let audioCtx: any = null;
  let analyser: any = null;
  let data: Uint8Array | null = null;
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (Ctx) {
      audioCtx = new Ctx();
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      audioCtx.createMediaStreamSource(stream).connect(analyser);
      data = new Uint8Array(analyser.frequencyBinCount);
    }
  } catch {
    analyser = null;
  }

  const chunks: BlobPart[] = [];
  const recorder = new (window as any).MediaRecorder(stream);
  recorder.ondataavailable = (e: any) => {
    if (e.data && e.data.size) chunks.push(e.data);
  };
  recorder.start();

  const release = () => {
    stream.getTracks().forEach(t => t.stop());
    try {
      audioCtx?.close();
    } catch {
      /* already closed */
    }
  };

  return {
    level: () => {
      if (!analyser || !data) return 0;
      analyser.getByteTimeDomainData(data);
      // Root-mean-square around the 128 midpoint, scaled to something that
      // reads as "loud" for ordinary speech rather than for shouting.
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      return Math.min(1, Math.sqrt(sum / data.length) * 4);
    },
    stop: () =>
      new Promise<Blob | null>(resolve => {
        const done = () => {
          release();
          resolve(chunks.length ? new Blob(chunks, {type: 'audio/webm'}) : null);
        };
        if (recorder.state === 'inactive') return done();
        recorder.onstop = done;
        try {
          recorder.stop();
        } catch {
          done();
        }
      }),
    cancel: () => {
      try {
        if (recorder.state !== 'inactive') recorder.stop();
      } catch {
        /* nothing to stop */
      }
      release();
    },
  };
}

/** Is voice input possible in this browser at all? */
export function canRecord(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as any).MediaRecorder === 'function' &&
    !!navigator?.mediaDevices?.getUserMedia
  );
}
